/**
 * Work Activity Submission Form
 * Allows members to submit monthly work hours across different activity types
 * Trust Marketplace - Work Documentation System
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Briefcase,
  GraduationCap,
  Heart,
  Users,
  Search,
  CheckCircle2,
  AlertCircle,
  Upload
} from 'lucide-react';

interface WorkActivityFormProps {
  enrollmentId: number;
  memberId: number;
  onSuccess?: () => void;
}

interface ActivityHours {
  employment_hours: number;
  education_hours: number;
  volunteer_hours: number;
  job_training_hours: number;
  job_search_hours: number;
  community_service_hours: number;
}

const WorkActivitySubmissionForm: React.FC<WorkActivityFormProps> = ({
  enrollmentId,
  memberId,
  onSuccess
}) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [hours, setHours] = useState<ActivityHours>({
    employment_hours: 0,
    education_hours: 0,
    volunteer_hours: 0,
    job_training_hours: 0,
    job_search_hours: 0,
    community_service_hours: 0
  });
  const [employerName, setEmployerName] = useState('');
  const [educationInstitution, setEducationInstitution] = useState('');
  const [volunteerOrganization, setVolunteerOrganization] = useState('');
  const [trainingProgramName, setTrainingProgramName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const totalHours = Object.values(hours).reduce((sum, h) => sum + h, 0);
  const meetsRequirement = totalHours >= 80;

  const handleHoursChange = (field: keyof ActivityHours, value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 744) return; // Max hours in a month
    setHours(prev => ({ ...prev, [field]: numValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const url = getWeaveinApiUrl('/api/work-compliance/submit-work-activity');

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: {
          enrollment_id: enrollmentId,
          month,
          year,
          ...hours,
          employer_name: employerName || undefined,
          education_institution: educationInstitution || undefined,
          volunteer_organization: volunteerOrganization || undefined,
          training_program_name: trainingProgramName || undefined,
          notes: notes || undefined
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Submission failed: ${response.status}`);
      }

      const result = await response.json();
      setSuccess(true);

      // Reset form after successful submission
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          window.location.href = `/work-compliance/dashboard/${memberId}`;
        }
      }, 2000);

    } catch (err) {
      console.error('Error submitting work activity:', err);
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="p-8 max-w-2xl mx-auto mt-8">
        <div className="text-center">
          <CheckCircle2 className="mx-auto text-green-600 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-green-600 mb-2">{t('workcompliance.submission_successful', 'Submission Successful!')}</h2>
          <p className="text-gray-600 mb-4">
            Your work hours for {month}/{year} have been submitted for verification.
          </p>
          {totalHours >= 80 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-green-800">🎉 Congratulations!</p>
              <p className="text-sm text-green-700">
                You've submitted {totalHours} hours - exceeding the 80-hour requirement!
                You may be eligible for compliance incentives.
              </p>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                You submitted {totalHours} hours ({80 - totalHours} short of the 80-hour requirement).
                Consider adding more qualifying activities.
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500">
            {t('workcompliance.redirecting_to_dashboard', 'Redirecting to dashboard...')}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        {!tabbed && <h1 className="text-3xl font-bold">{t('workcompliance.submit_work_hours', 'Submit Work Hours')}</h1>}
        <p className="text-gray-600 mt-2">
          {t('workcompliance.report_your_qualifying_work_activities', 'Report your qualifying work activities for the month. Minimum 80 hours required.')}
        </p>
      </div>

      {error && (
        <Card className="p-4 mb-6 border-l-4 border-red-500 bg-red-50">
          <div className="flex items-center text-red-800">
            <AlertCircle className="mr-2" size={20} />
            <span>{error}</span>
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        {/* Month/Year Selection */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">{t('workcompliance.reporting_period', 'Reporting Period')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('workcompliance.month', 'Month')}</label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="w-full p-2 border rounded-lg"
                required
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>
                    {new Date(year, m - 1).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('workcompliance.year', 'Year')}</label>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-full p-2 border rounded-lg"
                required
              >
                {[2025, 2026, 2027, 2028].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Activity Hours */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">{t('workcompliance.activity_hours', 'Activity Hours')}</h2>
          <div className="space-y-6">
            {/* Employment */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Briefcase className="text-blue-600" size={24} />
                <div className="flex-1">
                  <label className="font-semibold">{t('workcompliance.employment', 'Employment')}</label>
                  <p className="text-sm text-gray-600">{t('workcompliance.w2_1099_or_selfemployment_hours', 'W-2, 1099, or self-employment hours')}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="744"
                  value={hours.employment_hours}
                  onChange={(e) => handleHoursChange('employment_hours', e.target.value)}
                  className="w-24 p-2 border rounded-lg text-right font-bold"
                  placeholder="0"
                />
                <span className="text-gray-600">hrs</span>
              </div>
              {hours.employment_hours > 0 && (
                <input
                  type="text"
                  value={employerName}
                  onChange={(e) => setEmployerName(e.target.value)}
                  placeholder={t('workcompliance.employer_name', 'Employer name')}
                  className="w-full p-2 border rounded text-sm"
                />
              )}
            </div>

            {/* Education */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <GraduationCap className="text-purple-600" size={24} />
                <div className="flex-1">
                  <label className="font-semibold">{t('workcompliance.education', 'Education')}</label>
                  <p className="text-sm text-gray-600">{t('workcompliance.college_trade_school_ged_program', 'College, trade school, GED program')}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="744"
                  value={hours.education_hours}
                  onChange={(e) => handleHoursChange('education_hours', e.target.value)}
                  className="w-24 p-2 border rounded-lg text-right font-bold"
                  placeholder="0"
                />
                <span className="text-gray-600">hrs</span>
              </div>
              {hours.education_hours > 0 && (
                <input
                  type="text"
                  value={educationInstitution}
                  onChange={(e) => setEducationInstitution(e.target.value)}
                  placeholder={t('workcompliance.schoolinstitution_name', 'School/institution name')}
                  className="w-full p-2 border rounded text-sm"
                />
              )}
            </div>

            {/* Volunteer Work */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Heart className="text-red-600" size={24} />
                <div className="flex-1">
                  <label className="font-semibold">{t('workcompliance.volunteer_work', 'Volunteer Work')}</label>
                  <p className="text-sm text-gray-600">{t('workcompliance.unpaid_community_service', 'Unpaid community service')}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="744"
                  value={hours.volunteer_hours}
                  onChange={(e) => handleHoursChange('volunteer_hours', e.target.value)}
                  className="w-24 p-2 border rounded-lg text-right font-bold"
                  placeholder="0"
                />
                <span className="text-gray-600">hrs</span>
              </div>
              {hours.volunteer_hours > 0 && (
                <input
                  type="text"
                  value={volunteerOrganization}
                  onChange={(e) => setVolunteerOrganization(e.target.value)}
                  placeholder={t('workcompliance.organization_name', 'Organization name')}
                  className="w-full p-2 border rounded text-sm"
                />
              )}
            </div>

            {/* Job Training */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Users className="text-green-600" size={24} />
                <div className="flex-1">
                  <label className="font-semibold">{t('workcompliance.job_training', 'Job Training')}</label>
                  <p className="text-sm text-gray-600">{t('workcompliance.vocational_training_workshops', 'Vocational training, workshops')}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="744"
                  value={hours.job_training_hours}
                  onChange={(e) => handleHoursChange('job_training_hours', e.target.value)}
                  className="w-24 p-2 border rounded-lg text-right font-bold"
                  placeholder="0"
                />
                <span className="text-gray-600">hrs</span>
              </div>
              {hours.job_training_hours > 0 && (
                <input
                  type="text"
                  value={trainingProgramName}
                  onChange={(e) => setTrainingProgramName(e.target.value)}
                  placeholder={t('workcompliance.training_program_name', 'Training program name')}
                  className="w-full p-2 border rounded text-sm"
                />
              )}
            </div>

            {/* Job Search */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Search className="text-orange-600" size={24} />
                <div className="flex-1">
                  <label className="font-semibold">{t('workcompliance.job_search', 'Job Search')}</label>
                  <p className="text-sm text-gray-600">{t('workcompliance.active_job_search_activities', 'Active job search activities')}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="744"
                  value={hours.job_search_hours}
                  onChange={(e) => handleHoursChange('job_search_hours', e.target.value)}
                  className="w-24 p-2 border rounded-lg text-right font-bold"
                  placeholder="0"
                />
                <span className="text-gray-600">hrs</span>
              </div>
            </div>

            {/* Community Service */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Users className="text-teal-600" size={24} />
                <div className="flex-1">
                  <label className="font-semibold">{t('workcompliance.community_service', 'Community Service')}</label>
                  <p className="text-sm text-gray-600">{t('workcompliance.community_engagement_activities', 'Community engagement activities')}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="744"
                  value={hours.community_service_hours}
                  onChange={(e) => handleHoursChange('community_service_hours', e.target.value)}
                  className="w-24 p-2 border rounded-lg text-right font-bold"
                  placeholder="0"
                />
                <span className="text-gray-600">hrs</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Total Hours Summary */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{t('workcompliance.total_hours', 'Total Hours')}</h3>
              <p className="text-sm text-gray-600">{t('workcompliance.minimum_80_hours_required', 'Minimum 80 hours required')}</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{totalHours.toFixed(1)}</div>
              <Badge className={meetsRequirement ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                {meetsRequirement ? '✓ Meets Requirement' : `${(80 - totalHours).toFixed(1)} hrs short`}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Notes */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Additional Notes (Optional)</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('workcompliance.any_additional_information_about_your', 'Any additional information about your work activities...')}
            className="w-full p-3 border rounded-lg"
            rows={4}
          />
        </Card>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="px-6 py-3 border rounded-lg hover:bg-gray-50 font-semibold"
            disabled={loading}
          >
            {t('workcompliance.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={loading || totalHours === 0}
            className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <LoadingSpinner size="small" />
                <span>{t('workcompliance.submitting', 'Submitting...')}</span>
              </>
            ) : (
              <>
                <Upload size={20} />
                <span>{t('workcompliance.submit_work_hours_1', 'Submit Work Hours')}</span>
              </>
            )}
          </button>
        </div>

        {totalHours > 0 && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>{t('workcompliance.next_steps', 'Next steps:')}</strong> After submitting, upload supporting documents (pay stubs,
              timesheets, letters) to help verify your hours. Your CBO will review within 3 business days.
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default WorkActivitySubmissionForm;
